/**
 * contact-form-capture.js — mirrors every Shopify storefront contact-form
 * submission to the dealer-portal so they show up in the daily digest +
 * any future CRM hook. Backwards-compatible: Shopify's own form action
 * still fires and enquiry@ still receives the notification email; this
 * runs in parallel as a sniff.
 *
 * Loaded by snippets/contact-form-capture.liquid via {{ 'contact-form-capture.js' | asset_url | script_tag }}
 *
 * Listener target: every <form action="/contact#…"> on the page (Shopify's
 * `{% form 'contact' %}` Liquid tag emits this exact action). One global
 * delegated listener so we don't have to retrofit each section/snippet.
 */
(function () {
  'use strict';

  var CFG = window.RC_CONTACT_CONFIG || {};
  var STORE = CFG.store || 'AU';
  var API_BASE = CFG.apiBase || 'https://api.rolacase.us';
  var API_KEY = CFG.apiKey || 'rola-dp-7f3k9x2m4p';
  var ENDPOINT = API_BASE + '/api/v1/contact-form';

  function isShopifyContactForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = (form.getAttribute('action') || '').toLowerCase();
    return action.indexOf('/contact') === 0 || action.indexOf('/contact#') >= 0;
  }

  function pickField(form, names) {
    for (var i = 0; i < names.length; i++) {
      var el = form.querySelector('[name="' + names[i] + '"]');
      if (el && typeof el.value === 'string' && el.value.trim() !== '') {
        return el.value.trim();
      }
    }
    return '';
  }

  // collectExtraContactFields scans every contact[*] field on the form,
  // skips the ones picked by name above, and returns the rest as a flat
  // map. Catches form-specific fields (address, cart_data, gallery_image,
  // etc.) without having to enumerate every theme form variant. Sophia
  // 2026-05-15: Simon Brown filled the quote-request form with selected
  // products + delivery address but the team email arrived empty because
  // our interceptor only grabbed the canonical 8 fields.
  var KNOWN_CONTACT_KEYS = (function () {
    var set = {};
    [
      'firstname', 'first_name', 'lastname', 'last_name', 'name',
      'email', 'phone', 'countrycode', 'country', 'country_code',
      'postcode', 'zip', 'body', 'message',
      'form_type'
    ].forEach(function (k) { set[k.toLowerCase()] = true; });
    return set;
  })();

  function collectExtraContactFields(form) {
    var extras = {};
    var fields = form.querySelectorAll('input[name^="contact["], textarea[name^="contact["]');
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      var nameAttr = el.name || '';
      var match = nameAttr.match(/^contact\[([^\]]+)\]$/);
      if (!match) continue;
      var keyRaw = match[1];
      var keyLower = keyRaw.toLowerCase();
      if (KNOWN_CONTACT_KEYS[keyLower]) continue;
      var val = (typeof el.value === 'string') ? el.value : '';
      if (val.length === 0) continue;
      // Cap per-field length to 20KB so a malicious or runaway hidden
      // input doesn't balloon the payload. cart_data is normally <5KB.
      if (val.length > 20000) val = val.slice(0, 20000) + '… [truncated]';
      extras[keyLower] = val;
    }
    return extras;
  }

  function buildPayload(form) {
    var payload = {
      store: STORE,
      first_name: pickField(form, ['contact[FirstName]', 'contact[first_name]', 'first_name']),
      last_name:  pickField(form, ['contact[LastName]', 'contact[last_name]', 'last_name']),
      name:       pickField(form, ['contact[Name]', 'contact[name]', 'name']),
      email:      pickField(form, ['contact[email]', 'email']),
      phone:      pickField(form, ['contact[phone]', 'contact[Phone]', 'phone']),
      country_code: pickField(form, ['contact[CountryCode]', 'contact[country]', 'country_code']),
      postcode:   pickField(form, ['contact[postcode]', 'contact[Postcode]', 'contact[zip]', 'postcode']),
      message:    pickField(form, ['contact[Body]', 'contact[body]', 'contact[message]', 'message']),
      // referrer = the previous page (e.g. where they clicked the link from)
      // submit_page = the page where they actually submitted the form
      // Both useful: referrer answers "what marketing channel?", submit_page
      // answers "which page on the site converted this lead?"
      referrer:   (typeof document !== 'undefined' ? document.referrer : '') || '',
      submit_page: (typeof window !== 'undefined' && window.location) ? window.location.href : ''
    };
    // extra_fields carries every other contact[*] field the form sends
    // (address, cart_data, gallery_caption, gallery_source, etc.). Backend
    // stores them in Raw JSON; renderer surfaces them in the email.
    var extras = collectExtraContactFields(form);
    if (Object.keys(extras).length > 0) {
      payload.extra_fields = extras;
    }
    return payload;
  }

  // collectFiles walks the form AND document for any <input type="file"> with
  // a selected file. Some sections (e.g. contact-card.liquid) render the
  // file input OUTSIDE the form (inside a sibling label with id="rc-file")
  // and rely on a custom JS layer to ferry the file along. To stay
  // resilient against that pattern, we scan the form first (the cheap
  // common case) and then scan the entire document for orphan file inputs
  // the user has actually selected files into. False-positive risk is
  // negligible: a file input only contributes if the user actively chose
  // a file, so an unrelated form on the same page won't accidentally upload.
  function collectFiles(form) {
    var out = [];
    var seen = new Set();
    var addFiles = function (inp) {
      if (seen.has(inp)) return;
      seen.add(inp);
      if (inp.files && inp.files.length) {
        for (var j = 0; j < inp.files.length; j++) {
          out.push(inp.files[j]);
        }
      }
    };
    var inForm = form.querySelectorAll('input[type="file"]');
    for (var i = 0; i < inForm.length; i++) addFiles(inForm[i]);
    // Document-wide scan for orphan file inputs the user has filled
    var anywhere = document.querySelectorAll('input[type="file"]');
    for (var k = 0; k < anywhere.length; k++) addFiles(anywhere[k]);
    return out;
  }

  // sumSize totals the bytes of all selected files. Used to refuse uploads
  // that would push the resulting email past Gmail's ~25MB cap and avoid
  // multipart parsing OOM on the server. We cap at 12MB on our side which
  // leaves headroom after base64 encoding (~33% inflation in MIME).
  function sumSize(files) {
    var n = 0;
    for (var i = 0; i < files.length; i++) n += files[i].size || 0;
    return n;
  }

  // recentSubmits is the in-memory dedup gate for layer 2. Keys are a hash
  // of email + name + message; values are timestamps. If the same key has
  // been seen within DEDUP_WINDOW_MS, we skip the fetch. Caught Antonello's
  // double-tap on 2026-05-15. The backend has its own dedup layer too —
  // this one stops the wasted HTTP round-trip when the duplicate is purely
  // client-side (rapid taps before button could be disabled).
  var recentSubmits = {};
  var DEDUP_WINDOW_MS = 90 * 1000;

  function dedupKey(payload) {
    return [
      String(payload.email || ''),
      String(payload.name || ''),
      String(payload.first_name || ''),
      String(payload.last_name || ''),
      String(payload.message || ''),
    ].join('|').slice(0, 500);
  }

  function isRecentDuplicate(key) {
    var now = Date.now();
    var seen = recentSubmits[key];
    if (seen && (now - seen) < DEDUP_WINDOW_MS) return true;
    recentSubmits[key] = now;
    // Garbage-collect old keys so the map doesn't grow forever in a
    // long-lived SPA session.
    for (var k in recentSubmits) {
      if ((now - recentSubmits[k]) > DEDUP_WINDOW_MS * 2) delete recentSubmits[k];
    }
    return false;
  }

  function capture(form) {
    var payload;
    try {
      payload = buildPayload(form);
    } catch (err) {
      // Never let our sniff break the user's submit.
      try { console && console.warn && console.warn('[contact-capture] buildPayload failed:', err); } catch (_) {}
      return;
    }
    if (!payload.email && !payload.phone && !payload.message) {
      // Empty / honey-pot submission — Shopify itself will reject this,
      // we don't need to record it.
      return;
    }
    if (isRecentDuplicate(dedupKey(payload))) {
      // Rapid double-tap on SEND. Backend would catch it too, but skipping
      // the round-trip is cheaper.
      try { console && console.info && console.info('[contact-capture] duplicate submit suppressed'); } catch (_) {}
      return;
    }

    var files = [];
    try { files = collectFiles(form); } catch (_) {}
    var MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;
    if (files.length && sumSize(files) > MAX_ATTACHMENT_BYTES) {
      try { console && console.warn && console.warn('[contact-capture] attachments exceed 12MB, sending without files'); } catch (_) {}
      files = []; // drop files when too large — still capture the lead, just without the file
    }

    try {
      var body, headers;
      if (files.length) {
        // Multipart path: build FormData with text fields + file blobs.
        // Browser sets the Content-Type with boundary automatically when
        // body is a FormData instance — DO NOT set Content-Type manually
        // or the boundary won't be included.
        var fd = new FormData();
        for (var k in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, k) && payload[k] != null && payload[k] !== '') {
            fd.append(k, String(payload[k]));
          }
        }
        for (var i = 0; i < files.length; i++) {
          fd.append('attachments', files[i], files[i].name || ('attachment-' + (i + 1)));
        }
        body = fd;
        headers = { 'X-API-Key': API_KEY };
      } else {
        // JSON path (existing behaviour for forms with no files).
        body = JSON.stringify(payload);
        headers = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };
      }
      // keepalive: true lets the request finish even after the page navigates.
      // No await — Shopify's form submit continues to fire as normal.
      // NOTE: keepalive has a 64KB limit per spec, browsers WILL drop the
      // request if body exceeds it. Multipart with a real file is usually
      // well over 64KB, so we skip keepalive for multipart and accept that
      // page navigation could cut off the request — capture is still
      // best-effort, the user's Shopify submit is unaffected either way.
      fetch(ENDPOINT, {
        method: 'POST',
        keepalive: files.length === 0,
        credentials: 'omit',
        headers: headers,
        body: body
      }).catch(function () { /* silent — don't break Shopify path */ });
    } catch (err) {
      try { console && console.warn && console.warn('[contact-capture] fetch failed:', err); } catch (_) {}
    }
    // Hand the identified email to the storefront tracker so future
    // events on this browser join cookie_id → email automatically.
    try {
      if (payload.email && window.RolacaseTrack && window.RolacaseTrack.identify) {
        window.RolacaseTrack.identify(payload.email);
      }
      if (window.RolacaseTrack && window.RolacaseTrack.track) {
        window.RolacaseTrack.track('contact_form', { email: payload.email || '' });
      }
    } catch (_) {}
  }

  // Delegated listener: works for forms added later by sectionRendering.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (isShopifyContactForm(form)) {
      capture(form);
    }
  }, true);
})();
